/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\GatewayConfigTabs.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Calls Routing module.
----------------------------------------------------------------*/


import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { FormBox } from '../../../components/ui/FormBox';
import type { GatewayFieldConfig, CategoryConfig } from '../types';

interface GatewayConfigTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  categories: CategoryConfig[];
  fieldConfig: Record<string, GatewayFieldConfig[]>;
  searchQuery: string;
  searchResults: Record<string, number>;
  renderField: (field: GatewayFieldConfig) => React.ReactNode;
  t: (key: string, fallback?: string) => string;
}

export function GatewayConfigTabs({
  activeTab, onTabChange, categories, fieldConfig,
  searchQuery, searchResults, renderField, t,
}: GatewayConfigTabsProps) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap gap-1">
          {categories.map((category) => {
            const Icon = category.icon;
            const hasMatches = Boolean(searchQuery.trim() && searchResults[category.key] > 0);
            const resultCount = searchResults[category.key] ?? 0;
            return (
              <TabsTrigger key={category.key} value={category.key} icon={<Icon className="w-4 h-4" />}>
                <span className="flex items-center gap-2">
                  {t(category.labelKey)}
                  {hasMatches && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold text-white bg-green-500 rounded-full">
                      {resultCount}
                    </span>
                  )}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {categories.map((category) => (
          <TabsContent key={category.key} value={category.key}>
            <FormBox title={t(category.labelKey)} icon={<category.icon className="h-5 w-5" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
                {(fieldConfig[category.key] ?? []).map((field) => {
                  if (field.hidden) {
                    return (
                      <div key={field.name} className="hidden">
                        {renderField(field)}
                      </div>
                    );
                  }

                  if (searchQuery.trim()) {
                    const label = t(`gateways.${field.name}`).toLowerCase();
                    const fsName = field.fsName?.toLowerCase() ?? '';
                    const matches =
                      label.includes(searchQuery.toLowerCase()) ||
                      field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      fsName.includes(searchQuery.toLowerCase());
                    if (!matches) return null;
                  }

                  return (
                    <React.Fragment key={field.name}>
                      {renderField(field)}
                    </React.Fragment>
                  );
                })}
              </div>
            </FormBox>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
